import type { Schemas } from '@kanos/api-client';
import { Skeleton, ThemeMenu } from '@kanos/ui';
import { AppFooter, MaintenanceBanner } from '@kanos/web-core';
import type { ReactNode } from 'react';
import { Mail, Phone } from 'lucide-react';
import { FirmBadge } from './branding';

type PortalBranding = Schemas['PortalBranding'];

/** The legal pages of the firm, once both are named - the footer links them. */
export function firmLegalOf(branding: PortalBranding | undefined) {
  return branding?.imprintUrl && branding.privacyUrl
    ? { imprintUrl: branding.imprintUrl, privacyUrl: branding.privacyUrl }
    : null;
}

/** A page of the portal: the firm in the header, the light/dark switch, the footer with its pages. */
export function PortalPage({
  branding,
  children,
}: {
  branding: PortalBranding | undefined;
  children: ReactNode;
}) {
  const legal = firmLegalOf(branding);
  return (
    <div className="min-h-dvh">
      <MaintenanceBanner />
      <header className="mx-auto flex max-w-2xl items-center justify-between gap-4 px-safe-4 pt-[max(1rem,env(safe-area-inset-top))] pb-4 sm:px-safe-6">
        <FirmBadge branding={branding} />
        <ThemeMenu />
      </header>
      <main className="mx-auto grid max-w-2xl gap-8 px-safe-4 pt-4 pb-[max(2rem,env(safe-area-inset-bottom))] sm:px-safe-6 sm:pt-8">
        {branding?.closed ? <PortalClosed firmName={branding.firmName} /> : children}
      </main>
      <FirmContact branding={branding} />
      <AppFooter
        className="mx-auto max-w-2xl px-safe-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:px-safe-6"
        forClients
        {...(legal ? { firmLegal: legal } : {})}
      />
    </div>
  );
}

const contactLink =
  'inline-flex min-h-11 items-center gap-2 rounded-sm font-semibold text-accent-text outline-none hover:underline focus-visible:outline-2 focus-visible:outline-focus';

/**
 * How the person reaches its firm - the phone and e-mail the firm states for its clients - on
 * every page, so a question never ends in the portal. Nothing while the firm stated neither.
 */
function FirmContact({ branding }: { branding: PortalBranding | undefined }) {
  if (!branding?.firmName || (branding.phone === null && branding.email === null)) return null;
  return (
    <aside aria-label="Kontakt zur Kanzlei" className="mx-auto max-w-2xl px-4 pb-6 sm:px-6">
      <div className="glass grid gap-2 rounded-lg p-4 sm:p-5">
        <p className="text-sm text-muted">
          Fragen zu Ihren Angaben oder zum Portal? {branding.firmName} hilft Ihnen gern weiter.
        </p>
        <div className="flex flex-col gap-x-6 sm:flex-row sm:flex-wrap">
          {branding.phone !== null && (
            <a href={`tel:${branding.phone.replace(/[^\d+]/g, '')}`} className={contactLink}>
              <Phone aria-hidden className="size-4" />
              <span className="sr-only">Telefon: </span>
              {branding.phone}
            </a>
          )}
          {branding.email !== null && (
            <a href={`mailto:${branding.email}`} className={contactLink}>
              <Mail aria-hidden className="size-4" />
              <span className="sr-only">E-Mail: </span>
              {branding.email}
            </a>
          )}
        </div>
      </div>
    </aside>
  );
}

/**
 * The firm no longer uses Kanos (ADR-045): its clients learn it calmly, on every page - with
 * the way to the firm itself, never a hint why.
 */
function PortalClosed({ firmName }: { firmName: string | null }) {
  return (
    <Lead title="Dieses Portal ist geschlossen">
      {firmName ?? 'Diese Kanzlei'} nutzt das Mandantenportal nicht mehr. Bitte wenden Sie sich mit
      Ihren Unterlagen und Fragen direkt an die Kanzlei.
    </Lead>
  );
}

/** The big title of a portal page, with a line under it. */
export function Lead({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="grid gap-3">
      <h1 className="text-4xl leading-[1.05] font-semibold tracking-[-0.04em] sm:text-5xl">
        {title}
      </h1>
      <p className="text-muted sm:text-lg">{children}</p>
    </div>
  );
}

/** Space held while the page asks the API, so nothing jumps. */
export const PageLoading = () => <Skeleton rounded="lg" className="h-96" />;
